import { useGetRequestWithdrawOwnListExportQuery } from 'services/requestWithdraw/queries/useGetRequestWithdrawOwnListExportQuery';
import toast from 'react-hot-toast';
import { isRequestError } from 'utils/requestError';
import { saveListExcel } from 'utils/tableExport/saveListExcel';
import { useGetCompanyAllQuery } from 'services/user/queries';
import type { ColumnDef } from '@tanstack/react-table';
import { Card, InputDateTime, InputSelect, TableListData } from 'components';
import { Button, InputWraper, useForm, IUseFormStructureItem } from 'modules';
import React, { useCallback, useState } from 'react';
import { useGetRequestWihtdrawOwn } from 'services';
import { GetOwnListWithdrawRequestEntity } from 'services/openapi';
import { columnsDefined, type FilterType, getInitFilterValues } from './config';
import { useLocation } from 'react-router-dom';
import * as Yup from 'yup';
import { convertSelectStatusOptions, convertVariables } from './config';
import './RequestWithdrawOwn.scss';

const RequestWithdrawOwnList = () => {
  const location = useLocation();
  const urlParams = new URLSearchParams(location.search);
  const initFilterValues = getInitFilterValues(urlParams);
  const { data: companyInfo } = useGetCompanyAllQuery();
  const companyOptions = [
    { label: 'Tất cả', value: '' },
    ...(companyInfo?.map((item) => ({
      label: item.compName,
      value: item.compId,
    })) || []),
  ];

  const [variables, setVariables] = useState<FilterType>(initFilterValues);

  const handleFilter = useCallback(
    (e: FilterType) => {
      setVariables({
        ...variables,
        ...e,
        // refreshData: variables.refreshData + 1,
      });
    },
    [variables],
  );

  const filterOptions: IUseFormStructureItem[] = [
    {
      name: 'companyId',
      label: 'Doanh nghiệp/HTX',
      defaultValue: initFilterValues.companyId,
    },
    {
      name: 'transactionStatus',
      label: 'Trạng thái',
      defaultValue: initFilterValues.transactionStatus,
      validate: Yup.string().required('Vui lòng chọn Trạng thái'),
    },
    {
      name: 'fromTime',
      label: 'Ngày & giờ bắt đầu',
      defaultValue: initFilterValues.fromTime,
      validate: Yup.string()
        .required('Vui lòng chọn Ngày & giờ bắt đầu')
        .test(
          'comp-fromdate-todate',
          'Ngày & giờ bắt đầu phải nhỏ hơn Ngày & giờ kết thúc',
          function (value) {
            if (value)
              return (
                new Date(value).getTime() <
                new Date(this.parent?.toTime).getTime()
              );
            return false;
          },
        )
        .test(
          'comp-fromdate-todate-60-days',
          'Khoảng cách Ngày & giờ bắt đầu và Ngày & giờ kết thúc không vượt quá 60 ngày',
          function (value) {
            if (value) {
              const dateEnd: Date = new Date(this.parent?.toTime);
              dateEnd.setHours(0, 0, 0, 0);
              dateEnd.setDate(dateEnd.getDate() - 60);
              const dateStart: Date = new Date(value);
              dateEnd.setHours(0, 0, 0, 0);
              return dateEnd.getTime() <= dateStart.getTime();
            }
            return false;
          },
        ),
    },
    {
      name: 'toTime',
      label: 'Ngày & giờ kết thúc',
      defaultValue: initFilterValues.toTime,
      validate: Yup.string()
        .required('Vui lòng chọn Ngày & giờ kết thúc')
        .test(
          'comp-fromdate-todate',
          'Ngày & giờ kết thúc phải lớn hơn Ngày & giờ bắt đầu',
          function (value) {
            if (value)
              return (
                new Date(this.parent?.fromTime).getTime() <
                new Date(value).getTime()
              );
            return false;
          },
        )
        .test(
          'comp-fromdate-todate-60-days',
          'Khoảng cách Ngày & giờ bắt đầu và Ngày & giờ kết thúc không vượt quá 60 ngày',
          function (value) {
            if (value) {
              const dateEnd: Date = new Date(value);
              dateEnd.setHours(0, 0, 0, 0);
              dateEnd.setDate(dateEnd.getDate() - 60);
              const dateStart: Date = new Date(this.parent?.fromTime);
              dateEnd.setHours(0, 0, 0, 0);
              return dateEnd.getTime() <= dateStart.getTime();
            }
            return false;
          },
        ),
    },
  ];

  const { getInputProps, handleSubmit } = useForm({
    structure: filterOptions,
    onSubmit: handleFilter,
  });

  const [dataTable, setDataTable] = useState<{ total?: number }>({});

  const { isFetching: isLoadingExport, refetch: refetchExport } =
    useGetRequestWithdrawOwnListExportQuery({
      ...convertVariables(variables),
      page: '1',
      pageSize: String(
        Number((dataTable as unknown as { total?: number })?.total ?? 0),
      ),
    });

  const handleExportExcel = async () => {
    if (isLoadingExport) return;
    try {
      const result = await refetchExport({ throwOnError: true });
      await saveListExcel(
        result.data?.data,
        columnsDefined as ColumnDef<GetOwnListWithdrawRequestEntity>[],
        'Danh_sach_yeu_cau_rut_diem_cua_toi',
        true,
      );
    } catch (error) {
      if (!isRequestError(error)) {
        toast.error(
          error instanceof Error ? error.message : 'Không thể tải danh sách',
        );
      }
    }
  };

  return (
    <>
      <Card className='container-fluid mb20'>
        <form onSubmit={handleSubmit}>
          <div className='row'>
            <div className='col-lg-3 col-12'>
              <InputWraper
                inputProps={getInputProps('companyId')}
                renderInput={(props) => (
                  <InputSelect {...props} options={companyOptions} />
                )}
              />
            </div>
            <div className='col-lg-3 col-12'>
              <InputWraper
                inputProps={getInputProps('transactionStatus')}
                isRequired
                renderInput={(props) => (
                  <InputSelect
                    {...props}
                    options={convertSelectStatusOptions()}
                  />
                )}
              />
            </div>

            <div className='col-lg-3 col-12'>
              <InputWraper
                inputProps={getInputProps('fromTime')}
                isRequired
                renderInput={InputDateTime}
              />
            </div>

            <div className='col-lg-3 col-12'>
              <InputWraper
                inputProps={getInputProps('toTime')}
                isRequired
                renderInput={InputDateTime}
              />
            </div>
          </div>
          <div className='textCenter mt16 wrapButtonFilter'>
            <Button label={'Tìm kiếm'} type='submit' buttonType='success' />
            <Button
              className='ml15'
              onClick={handleExportExcel}
              label={'Tải về DS'}
              type='button'
              buttonType='info'
              isLoading={isLoadingExport}
            />
          </div>
        </form>
      </Card>

      <TableListData<GetOwnListWithdrawRequestEntity>
        columns={columnsDefined as ColumnDef<GetOwnListWithdrawRequestEntity>[]}
        useQuery={useGetRequestWihtdrawOwn}
        callBackData={setDataTable}
        variables={variables}
        setVariables={setVariables}
        convertVariables={convertVariables}
        orderColumn
      />
    </>
  );
};

export default RequestWithdrawOwnList;
